/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import React from 'react'
import colors from '@sportninja/common/constants/appColors'

interface Props {
  /** The team question: heading, select and hint. */
  team: React.ReactNode
  /** The team official question. */
  identity: React.ReactNode
  /** The chosen team's summary; renders nothing until a team is chosen. */
  summary: React.ReactNode
}

/**
 * A team rep's two questions in the left column, the chosen team's summary in
 * the right — the empty half of the page beside two half-width selects.
 *
 * On a narrow screen it stacks, with the summary between the two questions so
 * it stays next to the team it describes.
 */
const TeamRepSelectionLayout = ({ team, identity, summary }: Props) => (
  <div css={containerStyle}>
    <div css={gridStyle}>
      <div css={teamAreaStyle}>{team}</div>
      <div css={summaryAreaStyle}>{summary}</div>
      <div css={identityAreaStyle}>{identity}</div>
    </div>
    {/* Closes off both columns, where `IdentitySection` would otherwise rule
        off only its own. */}
    <div css={dividerStyle} />
  </div>
)

export default TeamRepSelectionLayout

const containerStyle = css`
  display: flex;
  flex-direction: column;
  align-self: stretch;
`

/* The second row takes any extra height when the summary outgrows the left
   column, so the officials question stays right under the team one. */
const gridStyle = css`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  grid-template-rows: auto 1fr;
  grid-template-areas:
    'team summary'
    'identity summary';
  column-gap: 24px;
  align-items: start;

  @media (max-width: 768px) {
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: auto;
    grid-template-areas:
      'team'
      'summary'
      'identity';
  }
`

const teamAreaStyle = css`
  grid-area: team;
  min-width: 0;
`

/* Level with the team heading on desktop — `PageTitle`'s 24px top margin; spaced
   from the select above it when stacked. Collapses while there is no summary,
   so its margin does not open a gap between the two questions on a narrow
   screen. */
const summaryAreaStyle = css`
  grid-area: summary;
  min-width: 0;
  margin-top: 24px;

  &:empty {
    display: none;
  }
`

const identityAreaStyle = css`
  grid-area: identity;
  min-width: 0;
`

/* Matches `IdentitySection`'s own rule, which this replaces here. */
const dividerStyle = css`
  width: 100%;
  height: 1px;
  margin-top: 32px;
  background-color: ${colors.VERY_DARK_GRAYISH_BLUE};
`
